import { Stack } from "@mui/material";
import React from "react";

/**
 * @component ANChartJSWrapper
 * @description A wrapper component for Chart.js components that provides a custom,
 * bounds-aware tooltip. It positions the tooltip dynamically to ensure it remains
 * visible within the chart container.
 *
 * @param {object} props - The component's props.
 * @param {object} [props.chartContext] - The context object from the chart, typically containing
 * information about the tooltip's state, such as `tooltip.caretX`, `tooltip.caretY`, and `tooltip.opacity`.
 * @param {React.ReactNode} [props.customTooltipContent] - The content to be rendered inside the custom tooltip.
 * This is displayed when `chartContext` is available.
 * @param {object} [props.customTooltipProps] - Props to customize the appearance and behavior of the custom tooltip.
 * @param {React.ReactNode} props.children - The chart component itself that this wrapper will enhance
 * with the custom tooltip.
 *
 * @returns {JSX.Element} A React component that wraps the provided chart and displays a custom tooltip.
 *
 */
export default function ANChartJSWrapper({
  chartContext,
  customTooltipContent,
  customTooltipProps,
  children,
}) {
  const chartContainerRef = React.useRef(null);
  const tooltipRef = React.useRef(null);
  const { sx: CustomTooltipSx, ...otherCustomTooltipProps } = customTooltipProps || {};

  // Calculate tooltip position to keep it within container bounds
  const getTooltipPosition = () => {
    if (!chartContext || !chartContainerRef.current) return { left: 0, top: 0 };

    const containerRect = chartContainerRef.current.getBoundingClientRect();

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    let tooltipWidth = tooltipRect.width || 187;
    let tooltipHeight = tooltipRect.height || 120;

    let left = chartContext.tooltip.caretX;
    let top = chartContext.tooltip.caretY;

    // Keep tooltip within horizontal bounds
    if (left + tooltipWidth > containerRect.width) {
      left = containerRect.width - tooltipWidth - 10; // 10px padding from edge
    }
    if (left < 0) {
      left = 10; // 10px padding from left edge
    }

    // Keep tooltip within vertical bounds (position above the hover point)
    top = top - tooltipHeight - 10; // Position above with 10px gap
    if (top < 0) {
      top = chartContext.tooltip.caretY + 10; // Position below if no room above
    }

    return { left, top };
  };

  return (
    <Stack ref={chartContainerRef} sx={{ position: "relative", overflow: "hidden" }}>
      {children} {/* The chart content goes here */}
      {/* This invisible spacer creates the layout space needed for proper resize detection. For more information about "Responsive Charts" with ChartJS check https://www.chartjs.org/docs/latest/configuration/responsive.html#responsive-charts*/}
      <div style={{ marginBottom: "16px" }} />
      <Stack
        ref={tooltipRef}
        borderRadius="4px"
        sx={{
          transition: "all 0.3s ease",
          opacity: chartContext?.tooltip?.opacity || 0,
          position: "absolute", // Position relative to the chart container
          left: `${getTooltipPosition().left}px`,
          top: `${getTooltipPosition().top}px`,
          backgroundColor: "background.paper",
          boxShadow: 2,
          border: "1px solid",
          borderColor: "divider",
          zIndex: 1000,
          pointerEvents: "none",
          ...CustomTooltipSx,
        }}
        {...otherCustomTooltipProps}
      >
        {chartContext && customTooltipContent}
      </Stack>
    </Stack>
  );
}
